import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";
import {
  ChartBar, Users, FileText, CalendarBlank, UsersThree, SignOut, Gear,
  Money, Receipt, CalendarDots, Package, List, X as XIcon, Repeat, Signature, EnvelopeSimple, ClipboardText,
} from "@phosphor-icons/react";

const nav = [
  { to: "/", label: "Dashboard", icon: ChartBar, end: true, testid: "nav-dashboard" },
  { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "nav-quotes" },
  { to: "/documenti", label: "Documenti", icon: Signature, testid: "nav-documenti" },
  { to: "/allegati-a", label: "Allegato A", icon: ClipboardText, testid: "nav-allegati-a", roles: ["amministratore", "super_admin"] },
  { to: "/email", label: "Email", icon: EnvelopeSimple, testid: "nav-email", roles: ["amministratore", "super_admin"] },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clients" },
  { to: "/catalogo", label: "Catalogo", icon: Package, testid: "nav-catalogo", roles: ["amministratore", "super_admin"] },
  { to: "/incassi", label: "Incassi", icon: Money, testid: "nav-incassi" },
  { to: "/spese", label: "Spese", icon: Receipt, testid: "nav-spese" },
  { to: "/abbonamenti", label: "Abbonamenti", icon: Repeat, testid: "nav-abbonamenti", roles: ["amministratore", "super_admin"] },
  { to: "/calendario", label: "Calendario", icon: CalendarDots, testid: "nav-calendario" },
  { to: "/scadenze", label: "Scadenze", icon: CalendarBlank, testid: "nav-deadlines" },
  { to: "/utenti", label: "Utenti", icon: UsersThree, testid: "nav-users", roles: ["amministratore", "super_admin"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Gear, testid: "nav-settings", roles: ["super_admin"] },
];

const ROLE_BADGES = {
  super_admin: { label: "SUPER", cls: "bg-[#0033A0] text-white" },
  amministratore: { label: "ADMIN", cls: "bg-[#0A0A0A] text-white" },
  consulente: { label: "CONS", cls: "bg-[#008A3C] text-white" },
  utente_semplice: { label: "USER", cls: "bg-gray-300 text-gray-800" },
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Chiudi drawer al cambio rotta
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Blocca scroll body quando drawer aperto
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const appName = settings?.app_name || "Gestionale 360";
  const companyName = settings?.company?.ragione_sociale || "Sa.Gi Group";
  const logoUrl = settings?.company?.logo_url;
  const roleBadge = ROLE_BADGES[user?.role];
  const visibleNav = nav.filter(n => !n.roles || n.roles.includes(user?.role));

  const sidebarBrand = (
    <div className="px-5 py-5 border-b border-[#E2E8F0] flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {logoUrl ? (
          <div className="w-10 h-10 bg-white border border-[#E2E8F0] rounded-sm overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="brand-mark">
            <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
          </div>
        ) : (
          <div className="w-10 h-10 bg-[#0033A0] flex items-center justify-center rounded-sm flex-shrink-0">
            <span className="text-white font-display font-black text-sm">SG</span>
          </div>
        )}
        <div className="min-w-0">
          <div className="font-display text-sm font-bold tracking-tight leading-tight truncate" title={appName}>{appName}</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#64748B] mt-1 truncate">{companyName}</div>
        </div>
      </div>
      <button
        onClick={() => setDrawerOpen(false)}
        className="md:hidden p-1 -mr-1 text-[#64748B] hover:text-[#0A0A0A]"
        aria-label="Chiudi menu"
        data-testid="mobile-drawer-close"
      >
        <XIcon size={20} weight="bold" />
      </button>
    </div>
  );

  const sidebarNav = (
    <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto thin-scroll">
      {visibleNav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          data-testid={n.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-sm text-sm transition-all
            ${isActive
              ? "bg-[#0033A0] text-white"
              : "text-[#0A0A0A] hover:bg-[#F4F5F7] active:bg-[#E2E8F0]"}`
          }
        >
          <n.icon size={18} weight="bold" />
          <span className="font-medium">{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  const sidebarFooter = (
    <div className="px-3 py-3 border-t border-[#E2E8F0]">
      <div className="px-3 py-2 mb-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Accesso</span>
          {roleBadge && (
            <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-sm ${roleBadge.cls}`}>
              {roleBadge.label}
            </span>
          )}
        </div>
        <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
        <div className="text-xs text-[#64748B] truncate">{user?.email}</div>
      </div>
      <button
        onClick={handleLogout}
        data-testid="logout-btn"
        className="w-full flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-sm text-sm text-[#0A0A0A] hover:bg-[#F4F5F7] active:bg-[#E2E8F0] transition-all"
      >
        <SignOut size={17} weight="bold" />
        <span className="font-medium">Esci</span>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F4F5F7]">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#E2E8F0] bg-white">
        {sidebarBrand}
        {sidebarNav}
        {sidebarFooter}
      </aside>

      {/* Drawer mobile */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative w-72 max-w-[85vw] flex flex-col bg-white shadow-2xl animate-in slide-in-from-left duration-200">
            {sidebarBrand}
            {sidebarNav}
            {sidebarFooter}
          </aside>
        </div>
      )}

      <main className="flex-1 flex flex-col min-w-0">
        {/* Topbar mobile */}
        <header className="md:hidden sticky top-0 z-30 bg-white border-b border-[#E2E8F0] px-3 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-2 text-[#0A0A0A] hover:bg-[#F4F5F7] active:bg-[#E2E8F0] rounded-sm"
            aria-label="Apri menu"
            data-testid="mobile-menu-btn"
          >
            <List size={22} weight="bold" />
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-7 h-7 object-contain flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 bg-[#0033A0] flex items-center justify-center rounded-sm flex-shrink-0">
                <span className="text-white font-display font-black text-[10px]">SG</span>
              </div>
            )}
            <span className="font-display font-bold text-sm truncate">{appName}</span>
          </div>
          {roleBadge && (
            <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-sm flex-shrink-0 ${roleBadge.cls}`}>
              {roleBadge.label}
            </span>
          )}
        </header>
        <div className="flex-1 overflow-auto thin-scroll">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
